import { useState } from "react";
import { X, Copy, Check, ArrowLeft, ArrowRight } from "lucide-react";

const TERMINAL_IMG = "https://static.prod-images.emergentagent.com/jobs/1ff839d8-f836-489e-94e3-d503cbb03325/images/4cfbd478e24036fa062483330383c586954688eefaa0891b8d57277808112b52.jpeg";
const TOGGLE_IMG = "https://static.prod-images.emergentagent.com/jobs/1ff839d8-f836-489e-94e3-d503cbb03325/images/216a5ceaf278b046ad5d6ab09691faf1215292d8fcba67e9e55adf55f11aad52.jpeg";
const BELL_IMG = "https://static.prod-images.emergentagent.com/jobs/1ff839d8-f836-489e-94e3-d503cbb03325/images/62c7579014675a46dbd9c33db6c7751664e627fa20cc8074b7fb20484cbe6e8d.jpeg";

export default function IMessageTutorial({ cmd, onClose }) {
  const [i, setI] = useState(0);
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard?.writeText(cmd); setCopied(true); setTimeout(() => setCopied(false), 1500); };

  const steps = [
    {
      title: "1 · Get the file",
      img: null,
      body: (
        <>
          <p>In LifeOS (the panel behind this), click <b className="text-cyan-300">Pair this Mac</b>, then <b className="text-cyan-300">Download bridge</b>.</p>
          <p className="mt-2">A file called <span className="text-cyan-200 font-mono text-[11px]">LifeOS-iMessage-Bridge.command</span> lands in your <b>Downloads</b> folder. You don't open it directly — the next steps do.</p>
        </>
      ),
    },
    {
      title: "2 · Open the Terminal app",
      img: TERMINAL_IMG,
      body: (
        <>
          <p>Press <b className="text-cyan-300">⌘ Command + Space</b>, type <b>Terminal</b>, and press <b>Return</b>.</p>
          <p className="mt-2">A plain window with text opens (like the picture). That's totally normal — you won't type anything complicated.</p>
        </>
      ),
    },
    {
      title: "3 · Start it (one paste)",
      img: null,
      body: (
        <>
          <p>Click <b className="text-cyan-300">Copy</b> below, click into the Terminal window, paste with <b className="text-cyan-300">⌘ Command + V</b>, and press <b>Return</b>.</p>
          <div className="flex items-center gap-2 mt-2">
            <code className="flex-1 text-[11px] text-cyan-200 bg-[#05090f] border border-white/10 rounded px-2 py-1.5 break-all">{cmd}</code>
            <button data-testid="tutorial-copy" onClick={copy} className="text-[11px] text-cyan-200 border border-white/15 rounded px-2.5 py-1.5 inline-flex items-center gap-1 shrink-0 hover:bg-white/10">{copied ? <Check size={12} /> : <Copy size={12} />} {copied ? "Copied" : "Copy"}</button>
          </div>
          <p className="mt-2 text-zinc-400">You don't need to know what it means — it just launches the file you downloaded. ✅</p>
        </>
      ),
    },
    {
      title: "4 · Give it permission (the key step)",
      img: TOGGLE_IMG,
      body: (
        <>
          <p>macOS needs your OK to read Messages. A Settings window may pop up — <b>but if nothing opens, open it yourself:</b></p>
          <ol className="list-decimal pl-4 mt-2 space-y-1 marker:text-cyan-400">
            <li>Click the <b className="text-cyan-300"> Apple menu</b> (top-left) → <b>System Settings</b>.</li>
            <li>Click <b>Privacy &amp; Security</b> in the sidebar, then scroll to <b className="text-cyan-300">Full Disk Access</b>.</li>
            <li>Find <b className="text-cyan-300">Terminal</b> in the list and <b>flip its switch ON</b> (it turns green, like the picture).</li>
            <li>If it asks, click <b>Quit &amp; Reopen</b>.</li>
          </ol>
          <p className="mt-2 text-zinc-400">Don't see Terminal in the list? Click the <b>＋</b>, then open <b>Applications → Utilities → Terminal</b>.</p>
        </>
      ),
    },
    {
      title: "5 · You're done 🎉",
      img: BELL_IMG,
      body: (
        <>
          <p>Come back to LifeOS. Within about <b className="text-emerald-300">10 seconds</b> the <b>🔔 bell turns green</b> and your messages start showing up.</p>
          <p className="mt-2">You never have to do this again — it keeps syncing on its own. (Keep that Terminal window open, or use the one-click <b>auto-start</b> so you don't have to.)</p>
        </>
      ),
    },
  ];

  const s = steps[i];
  return (
    <div data-testid="imessage-tutorial" className="fixed inset-0 z-[90] grid place-items-center p-4 bg-black/80 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl bg-[#070c14] border border-cyan-400/30 shadow-[0_30px_80px_rgba(0,0,0,0.9)] overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <span className="text-sm font-tactical font-bold uppercase tracking-widest text-cyan-200">Set up iMessage · picture guide</span>
          <button data-testid="tutorial-close" onClick={onClose} className="text-zinc-400 hover:text-white"><X size={18} /></button>
        </div>

        {s.img && (
          <div className="px-4 pt-4">
            <img src={s.img} alt={s.title} className="w-full h-44 object-cover rounded-xl border border-white/10" />
          </div>
        )}

        <div className="px-4 py-4">
          <h3 className="text-base font-bold text-white">{s.title}</h3>
          <div className="text-[13px] text-zinc-300 leading-relaxed mt-2 space-y-1">{s.body}</div>
        </div>

        <div className="flex items-center justify-between gap-2 px-4 py-3 border-t border-white/10">
          <button data-testid="tutorial-prev" disabled={i === 0} onClick={() => setI((v) => Math.max(0, v - 1))} className="inline-flex items-center gap-1 text-[12px] text-zinc-300 disabled:opacity-30 hover:text-white"><ArrowLeft size={14} /> Back</button>
          <div className="flex gap-1.5">
            {steps.map((_, k) => <span key={k} className={`w-1.5 h-1.5 rounded-full ${k === i ? "bg-cyan-400" : "bg-white/20"}`} />)}
          </div>
          {i < steps.length - 1 ? (
            <button data-testid="tutorial-next" onClick={() => setI((v) => Math.min(steps.length - 1, v + 1))} className="inline-flex items-center gap-1 text-[12px] font-bold text-[#04070c] bg-cyan-400 hover:bg-cyan-300 rounded-lg px-3 py-1.5">Next <ArrowRight size={14} /></button>
          ) : (
            <button data-testid="tutorial-done" onClick={onClose} className="text-[12px] font-bold text-[#04120c] bg-emerald-400 hover:bg-emerald-300 rounded-lg px-3 py-1.5">Got it</button>
          )}
        </div>
      </div>
    </div>
  );
}
